<!--
  One conversation: the header with the contact, the channel badge and a menu; the messages
  grouped by day; the composer. It is shown in three places - the app-wide modal (App.vue,
  inModal: the header then holds the close button), the customer card's הודעות ומסרים tab and
  /messages (both beside LatestPlanupMultiMessages). A mail thread is read-only.

  The look follows the owner's mockup of 15/10/2026: round bubbles with the time and the
  delivery ticks inside, the contact's avatar beside their bubbles, "היום" / "אתמול" chips
  between the days, round tool buttons and a round send button in the composer. All of its CSS
  is here (index.html and App.vue held it before).
-->
<template>
    <div class="chatWrapper"
         :class="{ chatInModal: inModal }">


        <!-- nothing open yet: /messages before a row is picked, a card with no conversations -->
        <div v-if="!msgGroup"
             class="chatEmptyState">
            <div class="chatEmptyIcon"><i class="fa-regular fa-comments"></i></div>
            <div class="chatEmptyTitle">אין שיחה פתוחה</div>
            <div class="chatEmptyText">השיחות עם הלקוחות בוואטסאפ ובמייל מופיעות כאן</div>
        </div>


        <template v-else>

        <div class="chat-header">


            <!-- the menu at the start (right) of the row, the close button at its end -->
            <div class="chatHeaderMenu"
                 v-if="menuItems.length">

                <button type="button"
                        class="chatIconBtn chatHeaderMenuBtn"
                        title="אפשרויות"
                        @click.stop="menuOpen = !menuOpen">
                    <i class="fa-solid fa-ellipsis-vertical"></i>
                </button>

                <div v-show="menuOpen"
                     class="chatHeaderMenuList"
                     @click.stop>

                    <div v-for="item in menuItems"
                         :key="item.key"
                         class="chatHeaderMenuItem"
                         @click="runMenuItem(item)">
                        <i :class="item.icon"></i>
                        <span>{{ item.label }}</span>
                    </div>

                </div>

            </div>


            <!-- the photo + name open the contact's customer card (the page picks its latest event) -->
            <div class="chatHeaderContact"
                 :class="{ chatHeaderCustomerLink: canOpenCustomer }"
                 :title="canOpenCustomer ? 'מעבר לכרטיס הלקוח' : ''"
                 @click="openCustomer">

                <div class="chatAvatar">

                    <img @error="$handlers.customerHandler.onContactImageError($event, contact)"
                         :src="$handlers.customerHandler.getContactImage(contact)">

                    <!-- the channel badge keeps its .whatsapp / .email class: the tests and the
                         incoming-mail flow look for it -->
                    <span class="chatChannelBadge"
                          :class="typeOfChat"></span>

                </div>


                <div class="chatHeaderText"
                     v-if="contact">

                    <div class="chatname">
                        <span>{{ $handlers.customerHandler.getDisplayName(contact) }}</span>
                    </div>

                    <!-- messages are newest-first (the api sorts id DESC, a sent message is
                         unshifted), so the last message is messages[0] -->
                    <div class="chatHeaderSub"
                         v-if="messages.length">{{ lastMessageLabel }}</div>

                </div>

            </div>


            <button v-if="inModal"
                    type="button"
                    class="chatIconBtn chatHeaderClose"
                    data-bs-dismiss="modal"
                    title="סגירה">
                <i class="fa-solid fa-xmark"></i>
            </button>


        </div>





        <div ref="messageContainer"
             class="chatMessages custom-scrollbar"
             @scroll="onMessagesScroll">


            <template v-for="row in rows">


                <div v-if="row.day"
                     :key="row.key"
                     class="chatDaySeparator">
                    <span>{{ row.day }}</span>
                </div>


                <div v-else
                     :key="row.key"
                     class="chatRow"
                     :class="row.message.incomingFromCustomer ? 'chatRowIn' : 'chatRowOut'">


                    <img v-if="row.message.incomingFromCustomer"
                         class="chatRowAvatar"
                         :src="$handlers.customerHandler.getContactImage(contact)"
                         @error="$handlers.customerHandler.onContactImageError($event, contact)">

                    <!-- ours: the business logo (a message carries no user of its own - the
                         business sent it), or the business's letter when there is no logo -->
                    <img v-else
                         class="chatRowAvatar chatRowAvatarOurs"
                         :src="ourAvatar"
                         @error="ourAvatarFailed = true">


                    <div class="chatBubble"
                         :class="row.message.incomingFromCustomer ? 'chatIncomingMessage' : 'chatOutgoingMessage'">

                        <!-- a recording (voice note / audio file) plays in its bubble; a photo /
                             sticker is shown in it -->
                        <ChatVoicePlayer v-if="isAudio(row.message)"
                                         :src="row.message.attachmentUrl"></ChatVoicePlayer>

                        <img v-else-if="row.message.attachmentUrl"
                             :src="row.message.attachmentUrl"
                             class="chatMessagePicture"
                             title="פתיחת התמונה"
                             @load="onPictureLoaded"
                             @click="openPicture(row.message)">

                        <div v-if="messageText(row.message)"
                             class="chatMessageText">{{ messageText(row.message) }}</div>


                        <div class="chatBubbleMeta"
                             :class="row.message.incomingFromCustomer ? 'chatIncomingMessageInnerInfoTime' : 'chatOutgoingMessageInnerInfoTime'">

                            <span class="chatBubbleTime">{{ timeOf(row.message.created) }}</span>

                            <!-- whatsapp's ticks: one once it left, two (blue) once whatsapp
                                 acknowledged it (gotWhatsappAck - baileys' messages.update) -->
                            <span v-if="!row.message.incomingFromCustomer && typeOfChat == 'whatsapp'"
                                  class="chatBubbleStatus">

                                <i v-if="row.message.gotWhatsappAck"
                                   class="fa-solid fa-check-double chatTickDelivered"
                                   title="נמסר"></i>

                                <i v-else
                                   class="fa-solid fa-check chatTickSent"
                                   title="נשלח"></i>

                            </span>

                        </div>

                    </div>


                </div>


            </template>


        </div>






        <div class="chat-input-wrapper">


            <!-- an email thread is read-only for now: the composer below sends over whatsapp,
                 and answering by mail needs a send path that doesn't exist yet -->
            <div v-if="typeOfChat == 'email'"
                 class="chatFooterNotice">
                מייל נכנס - להשבה השתמשו בשליחת הודעה מכרטיס הלקוח.
            </div>


            <div v-if="typeOfChat != 'email' && !whatsappConnected"
                 class="chatFooterNotice">

                חשבון הואצאפ אינו מחובר - לחיבור לחצו
                <span class="chatFooterLink"
                      @click="$('.modal').modal('hide') && $router.push('/settings?activeTab=orginizationSettings_whatsappTab')">כאן</span>.
            </div>



            <div class="chat-input-inner-wrapper"
                 v-if="typeOfChat != 'email' && whatsappConnected">


                <!-- the send button at the start (right) of the row, the tools at its end. The
                     hidden words keep the buttons readable for a screen reader, and let the tests
                     find them by their text (שלח / בחרו תבנית) -->
                <button type="button"
                        class="chatSendBtn"
                        title="שליחה"
                        @click="sendMessage">
                    <i class="fa-regular fa-paper-plane"></i>
                    <span class="chatSrOnly">שלח</span>
                </button>


                <!-- a textarea, so a message can have lines: Enter sends, Shift+Enter starts a
                     new line (on a touch keyboard Enter is the new line and the button sends) -->
                <div class="chatComposerBox">
                    <textarea v-model="messageToSend"
                              ref="messageToSendInput"
                              rows="1"
                              class="chatComposer"
                              placeholder="כתבו הודעה כאן..."
                              @keydown.enter="onComposerEnter"
                              @input="fitComposer"></textarea>
                </div>


                <button type="button"
                        class="chatIconBtn chatToolBtn chatTemplateBtn"
                        title="בחרו תבנית"
                        @click="$refs.messageTemplatePopup.show()">
                    <i class="fa-regular fa-file-lines"></i>
                    <span class="chatSrOnly">בחרו תבנית</span>
                </button>


                <button type="button"
                        class="chatIconBtn chatToolBtn chatEmojiBtn"
                        :class="{ active: emojisOpen }"
                        title="אימוג'י"
                        @click.stop="emojisOpen = !emojisOpen">
                    <i class="fa-regular fa-face-smile"></i>
                </button>


                <div v-show="emojisOpen"
                     class="chatEmojiPanel"
                     @click.stop>
                    <span v-for="emoji in emojis"
                          :key="emoji"
                          class="chatEmoji"
                          @click="insertEmoji(emoji)">{{ emoji }}</span>
                </div>


                <MessageTemplatePopup @text="useTemplate($event)"
                                      ref="messageTemplatePopup"></MessageTemplatePopup>


            </div>




        </div>

        </template>

    </div>
</template>
<script>

import MessageTemplatePopup from '@/components/MessageTemplates/MessageTemplatePopup.vue';
import ChatVoicePlayer from '@/components/PlanupMultiMessages/ChatVoicePlayer.vue';
import TemplateFieldsHandler from '@/modules/TemplateFieldsHandler';
import { dayKey, dayLabel, timeOf, lastMessageLabel } from '@/helpers/chat-time';

//the texts baileys stores for media that has no caption - hidden once the media itself is on the bubble
const MEDIA_LABELS = ['📷 תמונה', '🙂 סטיקר', '🎤 הודעה קולית', '🎵 קובץ שמע'];

//the emoji panel: the ones a business writes to its customers with
const EMOJIS = ['😀', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎', '🤩', '🥳', '😅', '😉', '🙂', '😇', '🤗', '🤔', '😴', '😢', '😭', '😡', '🙏', '👍', '👎', '👏', '🙌', '🤝', '💪', '❤️', '💜', '💛', '💚', '🤍', '🔥', '✨', '🎉', '🎊', '🎈', '🎁', '🎂', '💐', '🌹', '☀️', '📅', '⏰', '📍', '📞', '✅', '❌'];

export default {
    name: 'PlanupMultiMessagesChat',
    props: ['msgGroup', 'inModal'],
    data() {
        return {
            messageToSend: '',
            typeOfChat: '',
            messages: [],
            contact: {},
            customerId: null,
            //follow new messages / loading pictures down, unless the user scrolled up to read
            stickToBottom: true,
            menuOpen: false,
            emojisOpen: false,
            emojis: EMOJIS,
            //the logo url exists for every account; a tenant without a logo gets a 404 on it
            ourAvatarFailed: false
        }
    },

    computed: {

        //no link to the card we're already on - the card's own communication tab, or the
        //global chat opened on top of it
        canOpenCustomer() {

            return !!this.customerId && !(this.$route.name == 'CustomerPage' && this.$route.params.id == this.customerId);
        },

        whatsappConnected() {

            let status = this.$store.state.whatsappStatus;

            return typeof status == 'string' && status.includes('0');
        },

        ourAvatar() {

            let logo = this.$store.state.logoImageUrl;

            if (logo && !this.ourAvatarFailed)
                return logo;

            let settings = this.$store.state.businessSettings || {};

            return this.$globals.getLetterImage(settings.bizName || '');
        },

        //"הודעה אחרונה היום בשעה 12:17"
        lastMessageLabel() {

            return this.messages.length ? lastMessageLabel(this.messages[0].created) : '';
        },

        //oldest first, with a day chip ("היום" / "אתמול" / the date) before each day's messages
        rows() {

            let rows = [];
            let lastDay = null;
            let list = this.messages.slice().reverse();

            for (let i = 0; i < list.length; i++) {

                let message = list[i];
                let day = dayKey(message.created);

                if (day && day != lastDay) {
                    rows.push({ key: 'day_' + i, day: dayLabel(message.created) });
                    lastDay = day;
                }

                rows.push({ key: 'm_' + i, message });
            }

            return rows;
        },

        menuItems() {

            let items = [];
            let contact = this.contact || {};

            if (this.canOpenCustomer)
                items.push({ key: 'customer', icon: 'fa-regular fa-address-card', label: 'מעבר לכרטיס הלקוח' });

            if (this.typeOfChat == 'whatsapp' && contact.phone) {
                items.push({ key: 'whatsapp', icon: 'fa-brands fa-whatsapp', label: 'פתיחה באפליקציית WhatsApp' });
                items.push({ key: 'copyPhone', icon: 'fa-regular fa-copy', label: 'העתקת מספר הטלפון' });
            }

            if (this.typeOfChat == 'email' && contact.email)
                items.push({ key: 'copyEmail', icon: 'fa-regular fa-copy', label: 'העתקת כתובת המייל' });

            return items;
        }
    },

    mounted() {

        //the menu and the emoji panel close on a click anywhere else
        document.addEventListener('click', this.onDocumentClick);
    },

    beforeDestroy() {

        document.removeEventListener('click', this.onDocumentClick);
    },

    methods: {

        timeOf,

        onDocumentClick() {

            this.menuOpen = false;
            this.emojisOpen = false;
        },

        runMenuItem(item) {

            this.menuOpen = false;

            let contact = this.contact || {};

            if (item.key == 'customer')
                this.openCustomer();

            if (item.key == 'whatsapp')
                window.open('https://wa.me/' + this.internationalPhone(contact.phone), '_blank');

            if (item.key == 'copyPhone')
                this.copyText(contact.phone);

            if (item.key == 'copyEmail')
                this.copyText(contact.email);
        },

        //0526655629 -> 972526655629 (a number already written with its country code is kept)
        internationalPhone(phone) {

            let digits = String(phone || '').replace(/\D/g, '');

            return digits.slice(0, 1) == '0' ? '972' + digits.slice(1) : digits;
        },

        copyText(text) {

            if (!text)
                return;

            let done = () => { if (typeof this.AlertSuccess == 'function') this.AlertSuccess('הועתק'); };

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => { });
                return;
            }

            //older browsers
            let holder = document.createElement('textarea');
            holder.value = text;
            document.body.appendChild(holder);
            holder.select();
            try { document.execCommand('copy'); done(); } catch (e) { }
            document.body.removeChild(holder);
        },

        //the emoji goes in at the caret, and the caret stays after it
        insertEmoji(emoji) {

            let textarea = this.$refs.messageToSendInput;
            let text = this.messageToSend || '';
            let start = textarea ? textarea.selectionStart : text.length;
            let end = textarea ? textarea.selectionEnd : text.length;

            if (typeof start != 'number') {
                start = text.length;
                end = text.length;
            }

            this.messageToSend = text.slice(0, start) + emoji + text.slice(end);

            this.$nextTick(() => {

                if (!textarea)
                    return;

                textarea.focus();
                textarea.selectionStart = textarea.selectionEnd = start + emoji.length;

                this.fitComposer();
            });
        },

        openCustomer() {

            if (!this.canOpenCustomer)
                return;

            //the global chat box (App.vue) closes its modal on this
            this.$emit('openCustomer');

            this.$router.push(`/customer/${this.customerId}`);
        },

        scrollToBottom() {

            this.stickToBottom = true;

            this.$nextTick(() => {
                if (this.$refs.messageContainer)
                    this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight;
            })

        },

        onMessagesScroll() {

            let container = this.$refs.messageContainer;

            if (container)
                this.stickToBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 60;
        },

        //a picture that finishes loading grows its bubble - keep the newest message in view,
        //unless the user scrolled up to read older ones
        onPictureLoaded() {

            if (this.stickToBottom)
                this.scrollToBottom();
        },

        openPicture(message) {

            window.open(message.attachmentUrl, '_blank');
        },

        //a photo, sticker or recording carries only its "📷 תמונה" / "🙂 סטיקר" /
        //"🎤 הודעה קולית" label as text - with the picture or the player on the bubble the label
        //is noise; a caption is still shown under it
        messageText(message) {

            if (message.attachmentUrl && MEDIA_LABELS.indexOf(message.message) > -1)
                return '';

            return message.message;
        },

        //a recording is told by its file's extension (the api names the S3 key by the format it sniffed)
        isAudio(message) {

            return !!message.attachmentUrl && /\.(ogg|opus|mp3|m4a|aac|amr|wav)(\?.*)?$/i.test(message.attachmentUrl);
        },

        //Enter sends, Shift+Enter is a new line. On a touch keyboard there is no Shift, so there
        //Enter is the new line and the send button sends. Enter that confirms an IME composition
        //(e.g. picking a suggestion) is not a send.
        onComposerEnter(event) {

            if (event.shiftKey || event.isComposing || event.keyCode == 229)
                return;

            if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
                return;

            event.preventDefault();

            this.sendMessage();
        },

        //grows the box with its lines, up to about six, then it scrolls
        fitComposer() {

            this.$nextTick(() => {

                let textarea = this.$refs.messageToSendInput;

                if (!textarea)
                    return;

                textarea.style.height = 'auto';
                textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
            });
        },

        //a template about a meeting ("נקבעה עבורך פגישה ב{{תאריך הפגישה}}...") is filled from the
        //customer's next meeting - the chat has no meeting of its own, and without it the date,
        //day and time went out empty
        useTemplate(text) {

            let contact = this.contact;

            let meetingLookup = Promise.resolve(null);

            if (TemplateFieldsHandler.usesMeetingFields(text)) {

                //the card is resolved from the contact a moment after the chat loads
                let customerIdLookup = this.customerId
                    ? Promise.resolve(this.customerId)
                    : (contact && contact.id ? this.$handlers.customerHandler.getContactCustomerId(contact.id) : Promise.resolve(null));

                meetingLookup = customerIdLookup
                    .then(customerId => TemplateFieldsHandler.nextMeetingOfCustomer(customerId))
                    .catch(() => null);
            }

            meetingLookup.then(meeting => {

                //the meeting's links (אישור / שינוי / ביטול) are signed by the server - asked only when used
                let linksLookup = meeting && meeting.id && TemplateFieldsHandler.usesMeetingLinkFields(text)
                    ? TemplateFieldsHandler.meetingLinksOf(meeting.id)
                    : Promise.resolve({});

                return linksLookup.then(links => {

                    //another conversation was opened meanwhile
                    if (this.contact !== contact)
                        return;

                    this.messageToSend = this.fillTemplateTokens(text, meeting, links);

                    this.fitComposer();
                });
            });
        },

        //fills {{parameter}} tokens of a chosen message template with the conversation
        //contact's values, so the final text shows in the input before sending.
        //meeting tokens come from `meeting` (the customer's next one); the chat has no event
        //context - those tokens (and meeting tokens with no meeting ahead) resolve to '' rather
        //than reaching the recipient as raw {{...}}. the customer, general and booking-link
        //tokens do resolve, from the contact this conversation is with
        fillTemplateTokens(text, meeting, meetingLinks) {

            let contactName = this.contact ? this.$handlers.customerHandler.getDisplayName(this.contact) : '';

            let mappings = TemplateFieldsHandler.buildFieldMappings(meeting || null, null, { contacts: this.contact ? [this.contact] : [] });

            mappings['שם איש הקשר'] = contactName || '';

            //{{קישור לאישור הפגישה}} and the like, of that meeting
            Object.assign(mappings, meetingLinks || {});

            return TemplateFieldsHandler.fillTemplate(text, mappings);
        },

        sendMessage() {

            if (!this.messageToSend)
                return;

            //this box only speaks whatsapp - without the guard an email thread would quietly
            //send the reply to the contact's phone instead (or crash when they have no phone)
            if (this.typeOfChat == 'email')
                return;

            //spaces and empty lines around the text are left over from typing, not part of it
            let text = this.messageToSend.replace(/^\s+|\s+$/g, '');

            if (!text)
                return;

            this.messages.unshift(this.$handlers.whatsappHandler.sendMessage(this.contact.phone, text));

            this.messageToSend = null;

            this.emojisOpen = false;

            this.fitComposer();

            this.scrollToBottom();

        }
    },

    watch: {

        messages: function () {
            this.scrollToBottom();
        },

        msgGroup: {
            immediate: true,
            handler: function (msgGroup, oldValue) {

                delete this.$handlers.whatsappHandler.activeChats[oldValue];

                this.customerId = null;
                this.menuOpen = false;
                this.emojisOpen = false;

                if (!msgGroup)
                    return;

                this.$handlers.planupMultiMessagesHandler.loadConversation(msgGroup).then(data => {

                    this.messages = data.messages;

                    this.contact = data.contact;

                    // this.typeOfChat = data.messages[0].typeOfMessage;
                    this.typeOfChat = msgGroup.slice(0, 8) == 'whatsapp' ? 'whatsapp' : 'email';

                    this.$handlers.whatsappHandler.activeChats[msgGroup] = this.messages;


                    this.scrollToBottom();

                    //whose card this is comes from the contact row - the customerId stamped on each
                    //message doesn't follow a contact that moved to another card. An unknown email
                    //address has no contact id, so no link
                    let contact = data.contact;

                    if (contact && contact.id)
                        this.$handlers.customerHandler.getContactCustomerId(contact.id).then(customerId => {

                            if (this.contact === contact)
                                this.customerId = customerId;
                        });

                })




            }
        },
    },
    components: { MessageTemplatePopup, ChatVoicePlayer }


}
</script>

<style>
/* ───────────── the frame ───────────── */

.chatWrapper {
    display: flex;
    flex-direction: column;
    background: #fff;
    direction: rtl;
    text-align: right;
}


/* ───────────── the header ───────────── */

.chatWrapper .chat-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid #eef0f6;
    background: #fff;
    position: relative;
}

.chatHeaderContact {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
}

.chatAvatar {
    position: relative;
    flex: none;
    width: 48px;
    height: 48px;
}

.chatAvatar img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid #eef0f6;
    display: block;
    background: #fff;
}

/* the channel, on the avatar's corner */
.chatChannelBadge,
.multiMessageChannel {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #25d366;
    color: #fff;
    border: 2px solid #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    line-height: 1;
}

.chatChannelBadge.whatsapp::after,
.multiMessageChannel.whatsapp::after {
    content: "\f232";
    font-family: "Font Awesome 6 Brands";
    font-weight: 400;
}

.chatChannelBadge.email,
.multiMessageChannel.email {
    background: #60b0f4;
}

.chatChannelBadge.email::after,
.multiMessageChannel.email::after {
    content: "\f0e0";
    font-family: "Font Awesome 6 Free";
    font-weight: 900;
    font-size: 9px;
}

.chatHeaderText {
    flex: 1 1 auto;
    min-width: 0;
}

.chatname {
    color: #2a3142;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chatHeaderSub {
    color: #8a90a0;
    font-size: 12px;
    margin-top: 2px;
}

.chatHeaderCustomerLink {
    cursor: pointer;
}

.chatHeaderCustomerLink:hover .chatname {
    color: #ab8ce4 !important;
}

.chatHeaderCustomerLink:hover .chatname span {
    text-decoration: underline;
}

/* round icon buttons: the header's menu + close, the composer's tools */
.chatIconBtn {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 17px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    position: relative;
    transition: background .15s, color .15s;
}

.chatIconBtn:hover {
    background: #f3f4f8;
    color: #2a3142;
}

.chatHeaderMenu {
    position: relative;
    flex: none;
}

.chatHeaderMenuList {
    position: absolute;
    top: 44px;
    right: 0;
    min-width: 230px;
    background: #fff;
    border: 1px solid #eef0f6;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(42, 49, 66, .15);
    padding: 6px;
    z-index: 20;
}

.chatHeaderMenuItem {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #2a3142;
    white-space: nowrap;
}

.chatHeaderMenuItem i {
    width: 18px;
    text-align: center;
    color: #8a90a0;
}

.chatHeaderMenuItem:hover {
    background: #f6f4fd;
    color: #6d5bd0;
}

.chatHeaderMenuItem:hover i {
    color: #6d5bd0;
}


/* ───────────── the messages ───────────── */

/* beside the conversation list (the card's tab, /messages) the chat card is as tall as the
   list card (calc(100vh - 285px)): that, less the header and the composer */
.chatMessages {
    height: calc(100vh - 429px);
    min-height: 280px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 14px 8px;
    background-color: #efe9e2;
    background-image: linear-gradient(rgba(255, 255, 255, .4), rgba(255, 255, 255, .4)), url(https://planup-il.s3.eu-central-1.amazonaws.com/whatsappbg_new.png);
}

.chatDaySeparator {
    text-align: center;
    margin: 4px 0 14px;
}

.chatDaySeparator span {
    display: inline-block;
    background: rgba(255, 255, 255, .92);
    color: #6b7280;
    font-size: 11px;
    padding: 4px 12px;
    border-radius: 999px;
    box-shadow: 0 1px 1px rgba(0, 0, 0, .06);
}

.chatRow {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
}

/* ours on the right (the row's start in RTL), theirs on the left with the avatar outside */
.chatRowOut {
    justify-content: flex-start;
}

.chatRowIn {
    flex-direction: row-reverse;
    justify-content: flex-start;
}

.chatRowAvatar {
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, .1);
    background: #fff;
}

/* a logo is usually a wordmark - shown whole, not cropped to the circle */
.chatRowAvatarOurs {
    object-fit: contain;
    padding: 2px;
}

.chatBubble {
    max-width: 78%;
    min-width: 92px;
    padding: 8px 12px 5px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.6;
    color: #2a3142;
    box-shadow: 0 1px 1px rgba(0, 0, 0, .06);
    word-break: break-word;
    overflow-wrap: anywhere;
    position: relative;
}

.chatIncomingMessage {
    background: #fff;
}

.chatOutgoingMessage {
    background: #d9fdd3;
}

/* a message keeps its line breaks - whatsapp text is plain text with \n */
.chatMessageText {
    white-space: pre-wrap;
}

/* the picture a photo / sticker message carries */
.chatMessagePicture {
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 260px;
    margin: 0 0 6px;
    border-radius: 10px;
    cursor: zoom-in;
}

/* the time (and ours: the ticks) at the bubble's bottom-left corner */
.chatBubbleMeta {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.4;
    color: #8a90a0;
    direction: ltr;
}

.chatBubbleStatus {
    font-size: 11px;
}

.chatTickSent {
    color: #9aa0ad;
}

.chatTickDelivered {
    color: #53bdeb;
}


/* ───────────── the composer ───────────── */

.chatWrapper .chat-input-wrapper {
    background: #fff;
    border-top: 1px solid #eef0f6;
    padding: 10px 12px;
}

.chatFooterNotice {
    text-align: center;
    color: #8a90a0;
    font-size: 13px;
    padding: 8px 0;
}

.chatFooterLink {
    color: #ab8ce4;
    cursor: pointer;
}

.chatWrapper .chat-input-inner-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    position: relative;
}

.chatSendBtn {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: #7d5fc4;
    color: #fff;
    font-size: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    position: relative;
    box-shadow: 0 6px 14px rgba(125, 95, 196, .35);
    transition: background .15s, transform .1s;
}

.chatSendBtn:hover {
    background: #6b4fb3;
}

.chatSendBtn:active {
    transform: scale(.96);
}

/* the plane glyph leans right - a nudge centers it */
.chatSendBtn i {
    position: relative;
    left: -1px;
}

.chatComposerBox {
    flex: 1 1 auto;
    min-width: 0;
    border: 1px solid #e3e5ee;
    border-radius: 14px;
    background: #fff;
    padding: 0 14px;
    transition: border-color .15s, box-shadow .15s;
}

.chatComposerBox:focus-within {
    border-color: #b9a6ef;
    box-shadow: 0 0 0 3px rgba(171, 140, 228, .18);
}

.chatComposer {
    display: block;
    width: 100%;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent;
    resize: none;
    overflow-y: auto;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    padding: 12px 0;
    height: 46px;
    max-height: 160px;
    color: #2a3142;
}

.chatComposer::placeholder {
    color: #9aa0ad;
}

.chatToolBtn {
    width: 46px;
    height: 46px;
    border: 1px solid #e3e5ee;
    background: #fff;
    color: #6b7280;
    margin-bottom: 1px;
}

.chatToolBtn:hover,
.chatToolBtn.active {
    background: #f6f4fd;
    color: #6d5bd0;
    border-color: #d9d0f5;
}

.chatEmojiPanel {
    position: absolute;
    bottom: 58px;
    left: 0;
    width: 296px;
    max-width: 100%;
    background: #fff;
    border: 1px solid #eef0f6;
    border-radius: 14px;
    box-shadow: 0 12px 30px rgba(42, 49, 66, .18);
    padding: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    z-index: 20;
    direction: ltr;
}

.chatEmoji {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    border-radius: 8px;
    cursor: pointer;
}

.chatEmoji:hover {
    background: #f3f4f8;
}

/* a text for screen readers (and the tests) only */
.chatSrOnly {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}


/* ───────────── no conversation open ───────────── */

.chatEmptyState {
    height: calc(100vh - 285px);
    min-height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #8a90a0;
    text-align: center;
    padding: 20px;
}

.chatEmptyIcon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #f3f4f8;
    color: #b9a6ef;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    margin-bottom: 8px;
}

.chatEmptyTitle {
    color: #2a3142;
    font-weight: 600;
    font-size: 15px;
}

.chatEmptyText {
    font-size: 13px;
}


/* ───────────── in the app-wide modal (App.vue) ───────────── */

.globalChatBoxModal > .modal-dialog > .modal-content {
    border: none;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(42, 49, 66, .28);
}

/* the direct body only - the template popup is a modal inside the chat and keeps its padding */
.globalChatBoxModal > .modal-dialog > .modal-content > .modal-body {
    padding: 0 !important;
}

.globalChatBoxModal .chatMessages {
    height: calc(100vh - 290px);
    max-height: 640px;
    min-height: 240px;
}

@media (max-width: 575.98px) {

    .globalChatBoxModal .chatMessages {
        height: calc(100vh - 215px);
        max-height: none;
    }
}


/* ───────────── beside the conversation list, on a phone ───────────── */

@media (max-width: 991.98px) {

    .chatConversationsCard {
        height: auto !important;
        max-height: 40vh;
        margin-bottom: 16px;
    }

    .chatWrapper:not(.chatInModal) .chatMessages {
        height: 55vh;
    }

    .chatWrapper:not(.chatInModal) .chatEmptyState {
        height: auto;
        min-height: 240px;
    }
}
</style>
